import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Paper,
  Divider,
  Grid,
  TextField,      
} from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { Rider } from '../../../services/riderService';

interface CreatePaylinkDialogProps {
  open: boolean;
  rider: Rider;
  pendingSd: number;
  onClose: () => void;
  onSuccess: () => void;
}

const CreatePaylinkDialog: React.FC<CreatePaylinkDialogProps> = ({
  open,
  rider,
  pendingSd,
  onClose,
  onSuccess,
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const maxAmount = pendingSd ?? 0;

  // Form state
  const [amount, setAmount] = useState('');
  const [merchantOrderId, setMerchantOrderId] = useState('');
  const [linkReceiverPhone, setLinkReceiverPhone] = useState('');
  const [linkReceiverMail, setLinkReceiverMail] = useState('');
  const [expireAt, setExpireAt] = useState<Date | null>(null);

  // Auto-fill rider data
  const publicRiderId = rider.publicRiderId || '';
  const phone = rider.phone || '';
  const name = rider.name || '';
  const city = rider.city || '';
  const clientId = rider.clientRiderId || '';
  const clientName = rider.clientName || '';

  const readOnlyFieldSx = {
    '& .MuiFilledInput-root.Mui-disabled': {
      bgcolor: 'action.disabledBackground',
      color: 'text.primary',
      WebkitTextFillColor: 'inherit',
    },
    '& .MuiInputLabel-root.Mui-disabled': {
      color: 'text.secondary',
    },
    '& .MuiFilledInput-input.Mui-disabled': {
      color: 'text.primary',
      WebkitTextFillColor: 'inherit',
    },
  };

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setAmount('');
      setMerchantOrderId('');
      setLinkReceiverPhone('');
      setLinkReceiverMail('');
      setExpireAt(null);
      setError(null);

      // Auto-generate merchant order ID
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8).toUpperCase();
      setMerchantOrderId(`SD-${rider.publicRiderId || rider.id}-${timestamp}-${randomId}`);
    }
  }, [open, rider]);

  const handleSubmit = async () => {
    if (!amount || !merchantOrderId || !linkReceiverPhone || !expireAt) {
      setError('Amount, merchant order ID, link receiver phone, and expiration are required');
      return;
    }

    if (!clientId || !clientName) {
      setError('Rider must have client rider ID and client name assigned. Please assign a client to this rider first.');
      return;
    }

    if (parseFloat(amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    if (maxAmount <= 0) {
      setError('Rider has no pending SD available to create a paylink');
      return;
    }

    if (parseFloat(amount) > maxAmount) {
      setError(`Amount cannot exceed rider's pending SD of ₹${maxAmount.toFixed(2)}`);
      return;
    }

    if (expireAt.getTime() <= Date.now()) {
      setError('Expiration date must be in the future');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        publicRiderId,
        phone,
        name,
        city,
        clientId,
        clientName,
        amount: parseFloat(amount),
        merchantOrderId,
        linkReceiverPhone,
        linkReceiverMail,
        expireAt: expireAt.getTime(), // Convert to Unix timestamp in milliseconds
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/v1/security-deposit/riders/${rider.id}/paylink`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.data.message || 'Failed to create paylink');
      }
    } catch (error: any) {
      console.error('Error creating paylink:', error);
      setError(error.response?.data?.message || error.message || 'Failed to create paylink');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon />
          Create Security Deposit Paylink
        </Box>
      </DialogTitle>

      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Auto-filled Rider Information */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Rider Information (Auto-filled)
            </Typography>
            {(!clientId || !clientName) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Client rider ID and client name are missing. Please assign a client to this rider before creating a paylink.
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Public Rider ID"
                  value={publicRiderId}
                  disabled
                  size="small"
                  variant="filled"
                  sx={readOnlyFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={phone}
                  disabled
                  size="small"
                  variant="filled"
                  sx={readOnlyFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={name}
                  disabled
                  size="small"
                  variant="filled"
                  sx={readOnlyFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={city}
                  disabled
                  size="small"
                  variant="filled"
                  sx={readOnlyFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Client Rider ID"
                  value={clientId}
                  disabled
                  size="small"
                  variant="filled"
                  error={!clientId}
                  sx={readOnlyFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Client Name"
                  value={clientName}
                  disabled
                  size="small"
                  variant="filled"
                  error={!clientName}
                  sx={readOnlyFieldSx}
                />
              </Grid>
            </Grid>
          </Paper>

          <Divider />

          {/* User Input Fields */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Paylink Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Amount (₹)"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={
                    maxAmount > 0
                      ? `Pending SD available: ₹${maxAmount.toLocaleString()}`
                      : 'Rider has no pending SD available to create a paylink'
                  }
                  error={maxAmount <= 0 || (amount !== '' && parseFloat(amount) > maxAmount)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Merchant Order ID"
                  value={merchantOrderId}
                  onChange={(e) => setMerchantOrderId(e.target.value)}
                  required
                  size="small"
                  helperText="Auto-generated, can be modified"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Link Receiver Phone"
                  value={linkReceiverPhone}
                  onChange={(e) => setLinkReceiverPhone(e.target.value)}
                  required
                  size="small"
                  placeholder="Enter phone number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Link Receiver Email (optional)"
                  type="email"
                  value={linkReceiverMail}
                  onChange={(e) => setLinkReceiverMail(e.target.value)}
                  size="small"
                  placeholder="Enter email address"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Expiration Date & Time"
                  value={expireAt}
                  onChange={(newValue: Date | null) => setExpireAt(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: 'small',
                      helperText: 'Select when the paylink should expire'
                    }
                  }}
                  minDateTime={new Date()}
                />
              </Grid>
            </Grid>
          </Paper>
        </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !amount ||
            !merchantOrderId ||
            !linkReceiverPhone ||
            !expireAt ||
            !clientId ||
            !clientName ||
            maxAmount <= 0 ||
            (amount !== '' && parseFloat(amount) > maxAmount)
          }
          startIcon={loading ? <CircularProgress size={16} /> : <PaymentIcon />}
        >
          {loading ? 'Creating...' : 'Create Paylink'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePaylinkDialog;
